import _defineProperty from "/opt/buildhome/repo/gatsby/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import _toConsumableArray from "/opt/buildhome/repo/gatsby/node_modules/@babel/runtime/helpers/esm/toConsumableArray.js";
import _classCallCheck from "/opt/buildhome/repo/gatsby/node_modules/@babel/runtime/helpers/esm/classCallCheck.js";
import _createClass from "/opt/buildhome/repo/gatsby/node_modules/@babel/runtime/helpers/esm/createClass.js";
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
import sync, { cancelSync, flushSync } from 'framesync';
import { animate } from '../../animation/animate.mjs';
import { SubscriptionManager } from '../../utils/subscription-manager.mjs';
import { mixValues } from '../animation/mix-values.mjs';
import { copyBoxInto } from '../geometry/copy.mjs';
import { translateAxis, transformBox, applyBoxDelta, applyTreeDeltas } from '../geometry/delta-apply.mjs';
import { calcRelativePosition, calcRelativeBox, calcBoxDelta, calcLength, isNear } from '../geometry/delta-calc.mjs';
import { removeBoxTransforms } from '../geometry/delta-remove.mjs';
import { createBox, createDelta } from '../geometry/models.mjs';
import { getValueTransition } from '../../animation/utils/transitions.mjs';
import { boxEquals, isDeltaZero, aspectRatio } from '../geometry/utils.mjs';
import { NodeStack } from '../shared/stack.mjs';
import { scaleCorrectors } from '../styles/scale-correction.mjs';
import { buildProjectionTransform } from '../styles/transform.mjs';
import { eachAxis } from '../utils/each-axis.mjs';
import { hasTransform, hasScale, has2DTranslate } from '../utils/has-transform.mjs';
import { FlatTree } from '../../render/utils/flat-tree.mjs';
import { resolveMotionValue } from '../../value/utils/resolve-motion-value.mjs';
import { globalProjectionState } from './state.mjs';
import { delay } from '../../utils/delay.mjs';
import { mix } from '../../utils/mix.mjs';
var transformAxes = ["", "X", "Y", "Z"];
/**
 * We use 1000 as the animation target as 0-1000 maps better to pixels than 0-1
 * which has a noticeable difference in spring animations
 */
var animationTarget = 1000;
var id = 0;
function createProjectionNode(_ref) {
  var attachResizeListener = _ref.attachResizeListener,
    defaultParent = _ref.defaultParent,
    measureScroll = _ref.measureScroll,
    checkIsScrollRoot = _ref.checkIsScrollRoot,
    _resetTransform = _ref.resetTransform;
  return /*#__PURE__*/function () {
    function ProjectionNode(elementId) {
      var _this = this;
      var latestValues = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
      var parent = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : defaultParent === null || defaultParent === void 0 ? void 0 : defaultParent();
      _classCallCheck(this, ProjectionNode);
      /**
       * A unique ID generated for every projection node.
       */
      this.id = id++;
      /**
       * An id that represents a unique session instigated by startUpdate.
       */
      this.animationId = 0;
      /**
       * A Set containing all this component's children. This is used to iterate
       * through the children.
       *
       * TODO: This could be faster to iterate as a flat array stored on the root node.
       */
      this.children = new Set();
      /**
       * Options for the node. We use this to configure what kind of layout animations
       * we should perform (if any).
       */
      this.options = {};
      /**
       * We use this to detect when its safe to shut down part of a projection tree.
       * We have to keep projecting children for scale correction and relative projection
       * until all their parents stop performing layout animations.
       */
      this.isTreeAnimating = false;
      this.isAnimationBlocked = false;
      /**
       * Flag to true if we think this layout has been changed. We can't always know this,
       * currently we set it to true every time a component renders, or if it has a layoutDependency
       * if that has changed between renders. Additionally, components can be grouped by LayoutGroup
       * and if one node is dirtied, they all are.
       */
      this.isLayoutDirty = false;
      this.isTransformDirty = false;
      /**
       * Flag to true if we think the projection calculations for this or any
       * child might need recalculating as a result of an updated transform or layout animation.
       */
      this.isProjectionDirty = false;
      /**
       * Block layout updates for instant layout transitions throughout the tree.
       */
      this.updateManuallyBlocked = false;
      this.updateBlockedByResize = false;
      /**
       * Set to true between the start of the first `willUpdate` call and the end of the `didUpdate`
       * call.
       */
      this.isUpdating = false;
      /**
       * If this is an SVG element we currently disable projection transforms
       */
      this.isSVG = false;
      /**
       * Flag to true (during promotion) if a node doing an instant layout transition needs to reset
       * its projection styles.
       */
      this.needsReset = false;
      /**
       * Flags whether this node should have its transform reset prior to measuring.
       */
      this.shouldResetTransform = false;
      /**
       * An object representing the calculated contextual/accumulated/tree scale.
       * This will be used to scale calculcated projection transforms, as these are
       * calculated in screen-space but need to be scaled for elements to layoutly
       * make it to their calculated destinations.
       *
       * TODO: Lazy-init
       */
      this.treeScale = {
        x: 1,
        y: 1
      };
      /**
       *
       */
      this.eventHandlers = new Map();
      // Note: Currently only running on root node
      this.potentialNodes = new Map();
      this.checkUpdateFailed = function () {
        if (_this.isUpdating) {
          _this.isUpdating = false;
          _this.clearAllSnapshots();
        }
      };
      /**
       * This is a multi-step process as shared nodes might be of different depths. Nodes
       * are sorted by depth order, so we need to resolve the entire tree before moving to
       * the next step.
       */
      this.updateProjection = function () {
        _this.nodes.forEach(propagateDirtyNodes);
        _this.nodes.forEach(resolveTargetDelta);
        _this.nodes.forEach(calcProjection);
      };
      this.hasProjected = false;
      this.isVisible = true;
      this.animationProgress = 0;
      /**
       * Shared layout
       */
      // TODO Only running on root node
      this.sharedNodes = new Map();
      this.elementId = elementId;
      this.latestValues = latestValues;
      this.root = parent ? parent.root || parent : this;
      this.path = parent ? [].concat(_toConsumableArray(parent.path), [parent]) : [];
      this.parent = parent;
      this.depth = parent ? parent.depth + 1 : 0;
      elementId && this.root.registerPotentialNode(elementId, this);
      for (var i = 0; i < this.path.length; i++) {
        this.path[i].shouldResetTransform = true;
      }
      if (this.root === this) this.nodes = new FlatTree();
    }
    _createClass(ProjectionNode, [{
      key: "addEventListener",
      value: function addEventListener(name, handler) {
        if (!this.eventHandlers.has(name)) {
          this.eventHandlers.set(name, new SubscriptionManager());
        }
        return this.eventHandlers.get(name).add(handler);
      }
    }, {
      key: "notifyListeners",
      value: function notifyListeners(name) {
        var subscriptionManager = this.eventHandlers.get(name);
        for (var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
          args[_key - 1] = arguments[_key];
        }
        subscriptionManager === null || subscriptionManager === void 0 ? void 0 : subscriptionManager.notify.apply(subscriptionManager, args);
      }
    }, {
      key: "hasListeners",
      value: function hasListeners(name) {
        return this.eventHandlers.has(name);
      }
    }, {
      key: "registerPotentialNode",
      value: function registerPotentialNode(elementId, node) {
        this.potentialNodes.set(elementId, node);
      }
      /**
       * Lifecycles
       */
    }, {
      key: "mount",
      value: function mount(instance) {
        var _this2 = this;
        var isLayoutDirty = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
        var _a;
        if (this.instance) return;
        this.isSVG = instance instanceof SVGElement && instance.tagName !== "svg";
        this.instance = instance;
        var _this$options = this.options,
          layoutId = _this$options.layoutId,
          layout = _this$options.layout,
          visualElement = _this$options.visualElement;
        if (visualElement && !visualElement.current) {
          visualElement.mount(instance);
        }
        this.root.nodes.add(this);
        (_a = this.parent) === null || _a === void 0 ? void 0 : _a.children.add(this);
        this.elementId && this.root.potentialNodes.delete(this.elementId);
        if (isLayoutDirty && (layout || layoutId)) {
          this.isLayoutDirty = true;
        }
        if (attachResizeListener) {
          var cancelDelay;
          var resizeUnblockUpdate = function resizeUnblockUpdate() {
            return _this2.root.updateBlockedByResize = false;
          };
          attachResizeListener(instance, function () {
            _this2.root.updateBlockedByResize = true;
            cancelDelay && cancelDelay();
            cancelDelay = delay(resizeUnblockUpdate, 250);
            if (globalProjectionState.hasAnimatedSinceResize) {
              globalProjectionState.hasAnimatedSinceResize = false;
              _this2.nodes.forEach(finishAnimation);
            }
          });
        }
        if (layoutId) {
          this.root.registerSharedNode(layoutId, this);
        }
        // Only register the handler if it requires layout animation
        if (this.options.animate !== false && visualElement && (layoutId || layout)) {
          this.addEventListener("didUpdate", function (_ref2) {
            var delta = _ref2.delta,
              hasLayoutChanged = _ref2.hasLayoutChanged,
              hasRelativeTargetChanged = _ref2.hasRelativeTargetChanged,
              newLayout = _ref2.layout;
            var _a, _b, _c, _d, _e;
            if (_this2.isTreeAnimationBlocked()) {
              _this2.target = undefined;
              _this2.relativeTarget = undefined;
              return;
            }
            // TODO: Check here if an animation exists
            var layoutTransition = (_b = (_a = _this2.options.transition) !== null && _a !== void 0 ? _a : visualElement.getDefaultTransition()) !== null && _b !== void 0 ? _b : defaultLayoutTransition;
            var _visualElement$getPro = visualElement.getProps(),
              onLayoutAnimationStart = _visualElement$getPro.onLayoutAnimationStart,
              onLayoutAnimationComplete = _visualElement$getPro.onLayoutAnimationComplete;
            /**
             * The target layout of the element might stay the same,
             * but its position relative to its parent has changed.
             */
            var targetChanged = !_this2.targetLayout || !boxEquals(_this2.targetLayout, newLayout) || hasRelativeTargetChanged;
            /**
             * If the layout hasn't seemed to have changed, it might be that the
             * element is visually in the same place in the document but its position
             * relative to its parent has indeed changed. So here we check for that.
             */
            var hasOnlyRelativeTargetChanged = !hasLayoutChanged && hasRelativeTargetChanged;
            if (((_c = _this2.resumeFrom) === null || _c === void 0 ? void 0 : _c.instance) || hasOnlyRelativeTargetChanged || hasLayoutChanged && (targetChanged || !_this2.currentAnimation)) {
              if (_this2.resumeFrom) {
                _this2.resumingFrom = _this2.resumeFrom;
                _this2.resumingFrom.resumingFrom = undefined;
              }
              _this2.setAnimationOrigin(delta, hasOnlyRelativeTargetChanged);
              var animationOptions = _objectSpread(_objectSpread({}, getValueTransition(layoutTransition, "layout")), {}, {
                onPlay: onLayoutAnimationStart,
                onComplete: onLayoutAnimationComplete
              });
              if (visualElement.shouldReduceMotion) {
                animationOptions.delay = 0;
                animationOptions.type = false;
              }
              _this2.startAnimation(animationOptions);
            } else {
              /**
               * If the layout hasn't changed and we have an animation that hasn't started yet,
               * finish it immediately. Otherwise it will be animating from a location
               * that was probably never commited to screen and look like a jumpy box.
               */
              if (!hasLayoutChanged && _this2.animationProgress === 0) {
                finishAnimation(_this2);
              }
              _this2.isLead() && ((_e = (_d = _this2.options).onExitComplete) === null || _e === void 0 ? void 0 : _e.call(_d));
            }
            _this2.targetLayout = newLayout;
          });
        }
      }
    }, {
      key: "unmount",
      value: function unmount() {
        var _a, _b;
        this.options.layoutId && this.willUpdate();
        this.root.nodes.remove(this);
        (_a = this.getStack()) === null || _a === void 0 ? void 0 : _a.remove(this);
        (_b = this.parent) === null || _b === void 0 ? void 0 : _b.children.delete(this);
        this.instance = undefined;
        cancelSync.preRender(this.updateProjection);
      }
      // only on the root
    }, {
      key: "blockUpdate",
      value: function blockUpdate() {
        this.updateManuallyBlocked = true;
      }
    }, {
      key: "unblockUpdate",
      value: function unblockUpdate() {
        this.updateManuallyBlocked = false;
      }
    }, {
      key: "isUpdateBlocked",
      value: function isUpdateBlocked() {
        return this.updateManuallyBlocked || this.updateBlockedByResize;
      }
    }, {
      key: "isTreeAnimationBlocked",
      value: function isTreeAnimationBlocked() {
        var _a;
        return this.isAnimationBlocked || ((_a = this.parent) === null || _a === void 0 ? void 0 : _a.isTreeAnimationBlocked()) || false;
      }
      // Note: currently only running on root node
    }, {
      key: "startUpdate",
      value: function startUpdate() {
        var _a;
        if (this.isUpdateBlocked()) return;
        this.isUpdating = true;
        (_a = this.nodes) === null || _a === void 0 ? void 0 : _a.forEach(resetRotation);
        this.animationId++;
      }
    }, {
      key: "willUpdate",
      value: function willUpdate() {
        var shouldNotifyListeners = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : true;
        var _a, _b, _c;
        if (this.root.isUpdateBlocked()) {
          (_b = (_a = this.options).onExitComplete) === null || _b === void 0 ? void 0 : _b.call(_a);
          return;
        }
        !this.root.isUpdating && this.root.startUpdate();
        if (this.isLayoutDirty) return;
        this.isLayoutDirty = true;
        for (var i = 0; i < this.path.length; i++) {
          var node = this.path[i];
          node.shouldResetTransform = true;
          node.updateScroll("snapshot");
        }
        var _this$options2 = this.options,
          layoutId = _this$options2.layoutId,
          layout = _this$options2.layout;
        if (layoutId === undefined && !layout) return;
        var transformTemplate = (_c = this.options.visualElement) === null || _c === void 0 ? void 0 : _c.getProps().transformTemplate;
        this.prevTransformTemplateValue = transformTemplate === null || transformTemplate === void 0 ? void 0 : transformTemplate(this.latestValues, "");
        this.updateSnapshot();
        shouldNotifyListeners && this.notifyListeners("willUpdate");
      }
      // Note: Currently only running on root node
    }, {
      key: "didUpdate",
      value: function didUpdate() {
        var updateWasBlocked = this.isUpdateBlocked();
        // When doing an instant transition, we skip the layout update,
        // but should still clean up the measurements so that the next
        // snapshot could be taken correctly.
        if (updateWasBlocked) {
          this.unblockUpdate();
          this.clearAllSnapshots();
          this.nodes.forEach(clearMeasurements);
          return;
        }
        if (!this.isUpdating) return;
        this.isUpdating = false;
        /**
         * Search for and mount newly-added projection elements.
         *
         * TODO: Every time a new component is rendered we could search up the tree for
         * the closest mounted node and query from there rather than document.
         */
        if (this.potentialNodes.size) {
          this.potentialNodes.forEach(mountNodeEarly);
          this.potentialNodes.clear();
        }
        /**
         * Write
         */
        this.nodes.forEach(resetTransformStyle);
        /**
         * Read ==================
         */
        // Update layout measurements of updated children
        this.nodes.forEach(updateLayout);
        /**
         * Write
         */
        // Notify listeners that the layout is updated
        this.nodes.forEach(notifyLayoutUpdate);
        this.clearAllSnapshots();
        // Flush any scheduled updates
        flushSync.update();
        flushSync.preRender();
        flushSync.render();
      }
    }, {
      key: "clearAllSnapshots",
      value: function clearAllSnapshots() {
        this.nodes.forEach(clearSnapshot);
        this.sharedNodes.forEach(removeLeadSnapshots);
      }
    }, {
      key: "scheduleUpdateProjection",
      value: function scheduleUpdateProjection() {
        sync.preRender(this.updateProjection, false, true);
      }
    }, {
      key: "scheduleCheckAfterUnmount",
      value: function scheduleCheckAfterUnmount() {
        var _this3 = this;
        /**
         * If the unmounting node is in a layoutGroup and did trigger a willUpdate,
         * we manually call didUpdate to give a chance to the siblings to animate.
         * Otherwise, cleanup all snapshots to prevents future nodes from reusing them.
         */
        sync.postRender(function () {
          if (_this3.isLayoutDirty) {
            _this3.root.didUpdate();
          } else {
            _this3.root.checkUpdateFailed();
          }
        });
      }
      /**
       * Update measurements
       */
    }, {
      key: "updateSnapshot",
      value: function updateSnapshot() {
        if (this.snapshot || !this.instance) return;
        this.snapshot = this.measure();
      }
    }, {
      key: "updateLayout",
      value: function updateLayout() {
        var _a;
        if (!this.instance) return;
        // TODO: Incorporate into a forwarded scroll offset
        this.updateScroll();
        if (!(this.options.alwaysMeasureLayout && this.isLead()) && !this.isLayoutDirty) {
          return;
        }
        /**
         * When a node is mounted, it simply resumes from the prevLead's
         * snapshot instead of taking a new one, but the ancestors scroll
         * might have updated while the prevLead is unmounted. We need to
         * update the scroll again to make sure the layout we measure is
         * up to date.
         */
        if (this.resumeFrom && !this.resumeFrom.instance) {
          for (var i = 0; i < this.path.length; i++) {
            var node = this.path[i];
            node.updateScroll();
          }
        }
        var prevLayout = this.layout;
        this.layout = this.measure(false);
        this.layoutCorrected = createBox();
        this.isLayoutDirty = false;
        this.projectionDelta = undefined;
        this.notifyListeners("measure", this.layout.layoutBox);
        (_a = this.options.visualElement) === null || _a === void 0 ? void 0 : _a.notify("LayoutMeasure", this.layout.layoutBox, prevLayout === null || prevLayout === void 0 ? void 0 : prevLayout.layoutBox);
      }
    }, {
      key: "updateScroll",
      value: function updateScroll() {
        var phase = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : "measure";
        var needsMeasurement = Boolean(this.options.layoutScroll && this.instance);
        if (this.scroll && this.scroll.animationId === this.root.animationId && this.scroll.phase === phase) {
          needsMeasurement = false;
        }
        if (needsMeasurement) {
          this.scroll = {
            animationId: this.root.animationId,
            phase: phase,
            isRoot: checkIsScrollRoot(this.instance),
            offset: measureScroll(this.instance)
          };
        }
      }
    }, {
      key: "resetTransform",
      value: function resetTransform() {
        var _a;
        if (!_resetTransform) return;
        var isResetRequested = this.isLayoutDirty || this.shouldResetTransform;
        var hasProjection = this.projectionDelta && !isDeltaZero(this.projectionDelta);
        var transformTemplate = (_a = this.options.visualElement) === null || _a === void 0 ? void 0 : _a.getProps().transformTemplate;
        var transformTemplateValue = transformTemplate === null || transformTemplate === void 0 ? void 0 : transformTemplate(this.latestValues, "");
        var transformTemplateHasChanged = transformTemplateValue !== this.prevTransformTemplateValue;
        if (isResetRequested && (hasProjection || hasTransform(this.latestValues) || transformTemplateHasChanged)) {
          _resetTransform(this.instance, transformTemplateValue);
          this.shouldResetTransform = false;
          this.scheduleRender();
        }
      }
    }, {
      key: "measure",
      value: function measure() {
        var removeTransform = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : true;
        var pageBox = this.measurePageBox();
        var layoutBox = this.removeElementScroll(pageBox);
        /**
         * Measurements taken during the pre-render stage
         * still have transforms applied so we remove them
         * via calculation.
         */
        if (removeTransform) {
          layoutBox = this.removeTransform(layoutBox);
        }
        roundBox(layoutBox);
        return {
          animationId: this.root.animationId,
          measuredBox: pageBox,
          layoutBox: layoutBox,
          latestValues: {},
          source: this.id
        };
      }
    }, {
      key: "measurePageBox",
      value: function measurePageBox() {
        var visualElement = this.options.visualElement;
        if (!visualElement) return createBox();
        var box = visualElement.measureViewportBox();
        // Remove viewport scroll to give page-relative coordinates
        var scroll = this.root.scroll;
        if (scroll) {
          translateAxis(box.x, scroll.offset.x);
          translateAxis(box.y, scroll.offset.y);
        }
        return box;
      }
    }, {
      key: "removeElementScroll",
      value: function removeElementScroll(box) {
        var boxWithoutScroll = createBox();
        copyBoxInto(boxWithoutScroll, box);
        /**
         * Performance TODO: Keep a cumulative scroll offset down the tree
         * rather than loop back up the path.
         */
        for (var i = 0; i < this.path.length; i++) {
          var node = this.path[i];
          var scroll = node.scroll,
            options = node.options;
          if (node !== this.root && scroll && options.layoutScroll) {
            /**
             * If this is a new scroll root, we want to remove all previous scrolls
             * from the viewport box.
             */
            if (scroll.isRoot) {
              copyBoxInto(boxWithoutScroll, box);
              var rootScroll = this.root.scroll;
              /**
               * Undo the application of page scroll that was originally added
               * to the measured bounding box.
               */
              if (rootScroll) {
                translateAxis(boxWithoutScroll.x, -rootScroll.offset.x);
                translateAxis(boxWithoutScroll.y, -rootScroll.offset.y);
              }
            }
            translateAxis(boxWithoutScroll.x, scroll.offset.x);
            translateAxis(boxWithoutScroll.y, scroll.offset.y);
          }
        }
        return boxWithoutScroll;
      }
    }, {
      key: "applyTransform",
      value: function applyTransform(box) {
        var transformOnly = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
        var withTransforms = createBox();
        copyBoxInto(withTransforms, box);
        for (var i = 0; i < this.path.length; i++) {
          var node = this.path[i];
          if (!transformOnly && node.options.layoutScroll && node.scroll && node !== node.root) {
            transformBox(withTransforms, {
              x: -node.scroll.offset.x,
              y: -node.scroll.offset.y
            });
          }
          if (!hasTransform(node.latestValues)) continue;
          transformBox(withTransforms, node.latestValues);
        }
        if (hasTransform(this.latestValues)) {
          transformBox(withTransforms, this.latestValues);
        }
        return withTransforms;
      }
    }, {
      key: "removeTransform",
      value: function removeTransform(box) {
        var _a;
        var boxWithoutTransform = createBox();
        copyBoxInto(boxWithoutTransform, box);
        for (var i = 0; i < this.path.length; i++) {
          var node = this.path[i];
          if (!node.instance) continue;
          if (!hasTransform(node.latestValues)) continue;
          hasScale(node.latestValues) && node.updateSnapshot();
          var sourceBox = createBox();
          var nodeBox = node.measurePageBox();
          copyBoxInto(sourceBox, nodeBox);
          removeBoxTransforms(boxWithoutTransform, node.latestValues, (_a = node.snapshot) === null || _a === void 0 ? void 0 : _a.layoutBox, sourceBox);
        }
        if (hasTransform(this.latestValues)) {
          removeBoxTransforms(boxWithoutTransform, this.latestValues);
        }
        return boxWithoutTransform;
      }
      /**
       *
       */
    }, {
      key: "setTargetDelta",
      value: function setTargetDelta(delta) {
        this.targetDelta = delta;
        this.isProjectionDirty = true;
        this.root.scheduleUpdateProjection();
      }
    }, {
      key: "setOptions",
      value: function setOptions(options) {
        this.options = _objectSpread(_objectSpread(_objectSpread({}, this.options), options), {}, {
          crossfade: options.crossfade !== undefined ? options.crossfade : true
        });
      }
    }, {
      key: "clearMeasurements",
      value: function clearMeasurements() {
        this.scroll = undefined;
        this.layout = undefined;
        this.snapshot = undefined;
        this.prevTransformTemplateValue = undefined;
        this.targetDelta = undefined;
        this.target = undefined;
        this.isLayoutDirty = false;
      }
      /**
       * Frame calculations
       */
    }, {
      key: "resolveTargetDelta",
      value: function resolveTargetDelta() {
        var _a;
        /**
         * Once the dirty status of nodes has been spread through the tree, we also
         * need to check if we have a shared node of a different depth that has itself
         * been dirtied.
         */
        var lead = this.getLead();
        this.isProjectionDirty || (this.isProjectionDirty = lead.isProjectionDirty);
        this.isTransformDirty || (this.isTransformDirty = lead.isTransformDirty);
        /**
         * We don't use transform for this step of processing so we don't
         * need to check whether any nodes have changed transform.
         */
        if (!this.isProjectionDirty && !this.attemptToResolveRelativeTarget) return;
        var _this$options3 = this.options,
          layout = _this$options3.layout,
          layoutId = _this$options3.layoutId;
        /**
         * If we have no layout, we can't perform projection, so early return
         */
        if (!this.layout || !(layout || layoutId)) return;
        /**
         * If we don't have a targetDelta but do have a layout, we can attempt to resolve
         * a relativeParent. This will allow a component to perform scale correction
         * even if no animation has started.
         */
        // TODO If this is unsuccessful this currently happens every frame
        if (!this.targetDelta && !this.relativeTarget) {
          // TODO: This is a semi-repetition of further down this function, make DRY
          var relativeParent = this.getClosestProjectingParent();
          if (relativeParent && relativeParent.layout) {
            this.relativeParent = relativeParent;
            this.relativeTarget = createBox();
            this.relativeTargetOrigin = createBox();
            calcRelativePosition(this.relativeTargetOrigin, this.layout.layoutBox, relativeParent.layout.layoutBox);
            copyBoxInto(this.relativeTarget, this.relativeTargetOrigin);
          } else {
            this.relativeParent = this.relativeTarget = undefined;
          }
        }
        /**
         * If we have no relative target or no target delta our target isn't valid
         * for this frame.
         */
        if (!this.relativeTarget && !this.targetDelta) return;
        /**
         * Lazy-init target data structure
         */
        if (!this.target) {
          this.target = createBox();
          this.targetWithTransforms = createBox();
        }
        /**
         * If we've got a relative box for this component, resolve it into a target relative to the parent.
         */
        if (this.relativeTarget && this.relativeTargetOrigin && ((_a = this.relativeParent) === null || _a === void 0 ? void 0 : _a.target)) {
          calcRelativeBox(this.target, this.relativeTarget, this.relativeParent.target);
          /**
           * If we've only got a targetDelta, resolve it into a target
           */
        } else if (this.targetDelta) {
          if (Boolean(this.resumingFrom)) {
            // TODO: This is creating a new object every frame
            this.target = this.applyTransform(this.layout.layoutBox);
          } else {
            copyBoxInto(this.target, this.layout.layoutBox);
          }
          applyBoxDelta(this.target, this.targetDelta);
        } else {
          /**
           * If no target, use own layout as target
           */
          copyBoxInto(this.target, this.layout.layoutBox);
        }
        /**
         * If we've been told to attempt to resolve a relative target, do so.
         */
        if (this.attemptToResolveRelativeTarget) {
          this.attemptToResolveRelativeTarget = false;
          var _relativeParent = this.getClosestProjectingParent();
          if (_relativeParent && Boolean(_relativeParent.resumingFrom) === Boolean(this.resumingFrom) && !_relativeParent.options.layoutScroll && _relativeParent.target) {
            this.relativeParent = _relativeParent;
            this.relativeTarget = createBox();
            this.relativeTargetOrigin = createBox();
            calcRelativePosition(this.relativeTargetOrigin, this.target, _relativeParent.target);
            copyBoxInto(this.relativeTarget, this.relativeTargetOrigin);
          } else {
            this.relativeParent = this.relativeTarget = undefined;
          }
        }
      }
    }, {
      key: "getClosestProjectingParent",
      value: function getClosestProjectingParent() {
        if (!this.parent || hasScale(this.parent.latestValues) || has2DTranslate(this.parent.latestValues)) return undefined;
        if ((this.parent.relativeTarget || this.parent.targetDelta) && this.parent.layout) {
          return this.parent;
        } else {
          return this.parent.getClosestProjectingParent();
        }
      }
    }, {
      key: "calcProjection",
      value: function calcProjection() {
        var _a;
        var isProjectionDirty = this.isProjectionDirty,
          isTransformDirty = this.isTransformDirty;
        this.isProjectionDirty = this.isTransformDirty = false;
        var lead = this.getLead();
        var isShared = Boolean(this.resumingFrom) || this !== lead;
        var canSkip = true;
        if (isProjectionDirty) canSkip = false;
        if (isShared && isTransformDirty) canSkip = false;
        if (canSkip) return;
        var _this$options4 = this.options,
          layout = _this$options4.layout,
          layoutId = _this$options4.layoutId;
        /**
         * If this section of the tree isn't animating we can
         * delete our target sources for the following frame.
         */
        this.isTreeAnimating = Boolean(((_a = this.parent) === null || _a === void 0 ? void 0 : _a.isTreeAnimating) || this.currentAnimation || this.pendingAnimation);
        if (!this.isTreeAnimating) {
          this.targetDelta = this.relativeTarget = undefined;
        }
        if (!this.layout || !(layout || layoutId)) return;
        /**
         * Reset the corrected box with the latest values from box, as we're then going
         * to perform mutative operations on it.
         */
        copyBoxInto(this.layoutCorrected, this.layout.layoutBox);
        /**
         * Apply all the parent deltas to this box to produce the corrected box. This
         * is the layout box, as it will appear on screen as a result of the transforms of its parents.
         */
        applyTreeDeltas(this.layoutCorrected, this.treeScale, this.path, isShared);
        var target = lead.target;
        if (!target) return;
        if (!this.projectionDelta) {
          this.projectionDelta = createDelta();
          this.projectionDeltaWithTransform = createDelta();
        }
        var prevTreeScaleX = this.treeScale.x;
        var prevTreeScaleY = this.treeScale.y;
        var prevProjectionTransform = this.projectionTransform;
        /**
         * Update the delta between the corrected box and the target box before user-set transforms were applied.
         * This will allow us to calculate the corrected borderRadius and boxShadow to compensate
         * for our layout reprojection, but still allow them to be scaled correctly by the user.
         * It might be that to simplify this we may want to accept that user-set scale is also corrected
         * and we wouldn't have to keep and calc both deltas, OR we could support a user setting
         * to allow people to choose whether these styles are corrected based on just the
         * layout reprojection or the final bounding box.
         */
        calcBoxDelta(this.projectionDelta, this.layoutCorrected, target, this.latestValues);
        this.projectionTransform = buildProjectionTransform(this.projectionDelta, this.treeScale);
        if (this.projectionTransform !== prevProjectionTransform || this.treeScale.x !== prevTreeScaleX || this.treeScale.y !== prevTreeScaleY) {
          this.hasProjected = true;
          this.scheduleRender();
          this.notifyListeners("projectionUpdate", target);
        }
      }
    }, {
      key: "hide",
      value: function hide() {
        this.isVisible = false;
        // TODO: Schedule render
      }
    }, {
      key: "show",
      value: function show() {
        this.isVisible = true;
        // TODO: Schedule render
      }
    }, {
      key: "scheduleRender",
      value: function scheduleRender() {
        var notifyAll = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : true;
        var _a, _b, _c;
        (_b = (_a = this.options).scheduleRender) === null || _b === void 0 ? void 0 : _b.call(_a);
        notifyAll && ((_c = this.getStack()) === null || _c === void 0 ? void 0 : _c.scheduleRender());
        if (this.resumingFrom && !this.resumingFrom.instance) {
          this.resumingFrom = undefined;
        }
      }
    }, {
      key: "setAnimationOrigin",
      value: function setAnimationOrigin(delta) {
        var _this4 = this;
        var hasOnlyRelativeTargetChanged = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
        var _a, _b;
        var snapshot = this.snapshot;
        var snapshotLatestValues = (snapshot === null || snapshot === void 0 ? void 0 : snapshot.latestValues) || {};
        var mixedValues = _objectSpread({}, this.latestValues);
        var targetDelta = createDelta();
        this.relativeTarget = this.relativeTargetOrigin = undefined;
        this.attemptToResolveRelativeTarget = !hasOnlyRelativeTargetChanged;
        var relativeLayout = createBox();
        var isSharedLayoutAnimation = (snapshot === null || snapshot === void 0 ? void 0 : snapshot.source) !== ((_a = this.layout) === null || _a === void 0 ? void 0 : _a.source);
        var isOnlyMember = (((_b = this.getStack()) === null || _b === void 0 ? void 0 : _b.members.length) || 0) <= 1;
        var shouldCrossfadeOpacity = Boolean(isSharedLayoutAnimation && !isOnlyMember && this.options.crossfade === true && !this.path.some(hasOpacityCrossfade));
        this.animationProgress = 0;
        this.mixTargetDelta = function (latest) {
          var _a;
          var progress = latest / 1000;
          mixAxisDelta(targetDelta.x, delta.x, progress);
          mixAxisDelta(targetDelta.y, delta.y, progress);
          _this4.setTargetDelta(targetDelta);
          if (_this4.relativeTarget && _this4.relativeTargetOrigin && _this4.layout && ((_a = _this4.relativeParent) === null || _a === void 0 ? void 0 : _a.layout)) {
            calcRelativePosition(relativeLayout, _this4.layout.layoutBox, _this4.relativeParent.layout.layoutBox);
            mixBox(_this4.relativeTarget, _this4.relativeTargetOrigin, relativeLayout, progress);
          }
          if (isSharedLayoutAnimation) {
            _this4.animationValues = mixedValues;
            mixValues(mixedValues, snapshotLatestValues, _this4.latestValues, progress, shouldCrossfadeOpacity, isOnlyMember);
          }
          _this4.root.scheduleUpdateProjection();
          _this4.scheduleRender();
          _this4.animationProgress = progress;
        };
        this.mixTargetDelta(0);
      }
    }, {
      key: "startAnimation",
      value: function startAnimation(options) {
        var _this5 = this;
        var _a, _b;
        this.notifyListeners("animationStart");
        (_a = this.currentAnimation) === null || _a === void 0 ? void 0 : _a.stop();
        if (this.resumingFrom) {
          (_b = this.resumingFrom.currentAnimation) === null || _b === void 0 ? void 0 : _b.stop();
        }
        if (this.pendingAnimation) {
          cancelSync.update(this.pendingAnimation);
          this.pendingAnimation = undefined;
        }
        /**
         * Start the animation in the next frame to have a frame with progress 0,
         * where the target is the same as when the animation started, so we can
         * calculate the relative positions correctly for instant transitions.
         */
        this.pendingAnimation = sync.update(function () {
          globalProjectionState.hasAnimatedSinceResize = true;
          _this5.currentAnimation = animate(0, animationTarget, _objectSpread(_objectSpread({}, options), {}, {
            onUpdate: function onUpdate(latest) {
              var _a;
              _this5.mixTargetDelta(latest);
              (_a = options.onUpdate) === null || _a === void 0 ? void 0 : _a.call(options, latest);
            },
            onComplete: function onComplete() {
              var _a;
              (_a = options.onComplete) === null || _a === void 0 ? void 0 : _a.call(options);
              _this5.completeAnimation();
            }
          }));
          if (_this5.resumingFrom) {
            _this5.resumingFrom.currentAnimation = _this5.currentAnimation;
          }
          _this5.pendingAnimation = undefined;
        });
      }
    }, {
      key: "completeAnimation",
      value: function completeAnimation() {
        var _a;
        if (this.resumingFrom) {
          this.resumingFrom.currentAnimation = undefined;
          this.resumingFrom.preserveOpacity = undefined;
        }
        (_a = this.getStack()) === null || _a === void 0 ? void 0 : _a.exitAnimationComplete();
        this.resumingFrom = this.currentAnimation = this.animationValues = undefined;
        this.notifyListeners("animationComplete");
      }
    }, {
      key: "finishAnimation",
      value: function finishAnimation() {
        var _a;
        if (this.currentAnimation) {
          (_a = this.mixTargetDelta) === null || _a === void 0 ? void 0 : _a.call(this, animationTarget);
          this.currentAnimation.stop();
        }
        this.completeAnimation();
      }
    }, {
      key: "applyTransformsToTarget",
      value: function applyTransformsToTarget() {
        var lead = this.getLead();
        var targetWithTransforms = lead.targetWithTransforms,
          target = lead.target,
          layout = lead.layout,
          latestValues = lead.latestValues;
        if (!targetWithTransforms || !target || !layout) return;
        /**
         * If we're only animating position, and this element isn't the lead element,
         * then instead of projecting into the lead box we instead want to calculate
         * a new target that aligns the two boxes but maintains the layout shape.
         */
        if (this !== lead && this.layout && layout && shouldAnimatePositionOnly(this.options.animationType, this.layout.layoutBox, layout.layoutBox)) {
          target = this.target || createBox();
          var xLength = calcLength(this.layout.layoutBox.x);
          target.x.min = lead.target.x.min;
          target.x.max = target.x.min + xLength;
          var yLength = calcLength(this.layout.layoutBox.y);
          target.y.min = lead.target.y.min;
          target.y.max = target.y.min + yLength;
        }
        copyBoxInto(targetWithTransforms, target);
        /**
         * Apply the latest user-set transforms to the targetBox to produce the targetBoxFinal.
         * This is the final box that we will then project into by calculating a transform delta and
         * applying it to the corrected box.
         */
        transformBox(targetWithTransforms, latestValues);
        /**
         * Update the delta between the corrected box and the final target box, after
         * user-set transforms are applied to it. This will be used by the renderer to
         * create a transform style that will reproject the element from its layout layout
         * into the desired bounding box.
         */
        calcBoxDelta(this.projectionDeltaWithTransform, this.layoutCorrected, targetWithTransforms, latestValues);
      }
    }, {
      key: "registerSharedNode",
      value: function registerSharedNode(layoutId, node) {
        var _a, _b, _c;
        if (!this.sharedNodes.has(layoutId)) {
          this.sharedNodes.set(layoutId, new NodeStack());
        }
        var stack = this.sharedNodes.get(layoutId);
        stack.add(node);
        node.promote({
          transition: (_a = node.options.initialPromotionConfig) === null || _a === void 0 ? void 0 : _a.transition,
          preserveFollowOpacity: (_c = (_b = node.options.initialPromotionConfig) === null || _b === void 0 ? void 0 : _b.shouldPreserveFollowOpacity) === null || _c === void 0 ? void 0 : _c.call(_b, node)
        });
      }
    }, {
      key: "isLead",
      value: function isLead() {
        var stack = this.getStack();
        return stack ? stack.lead === this : true;
      }
    }, {
      key: "getLead",
      value: function getLead() {
        var _a;
        var layoutId = this.options.layoutId;
        return layoutId ? ((_a = this.getStack()) === null || _a === void 0 ? void 0 : _a.lead) || this : this;
      }
    }, {
      key: "getPrevLead",
      value: function getPrevLead() {
        var _a;
        var layoutId = this.options.layoutId;
        return layoutId ? (_a = this.getStack()) === null || _a === void 0 ? void 0 : _a.prevLead : undefined;
      }
    }, {
      key: "getStack",
      value: function getStack() {
        var layoutId = this.options.layoutId;
        if (layoutId) return this.root.sharedNodes.get(layoutId);
      }
    }, {
      key: "promote",
      value: function promote() {
        var _ref3 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
          needsReset = _ref3.needsReset,
          transition = _ref3.transition,
          preserveFollowOpacity = _ref3.preserveFollowOpacity;
        var stack = this.getStack();
        if (stack) stack.promote(this, preserveFollowOpacity);
        if (needsReset) {
          this.projectionDelta = undefined;
          this.needsReset = true;
        }
        if (transition) this.setOptions({
          transition: transition
        });
      }
    }, {
      key: "relegate",
      value: function relegate() {
        var stack = this.getStack();
        if (stack) {
          return stack.relegate(this);
        } else {
          return false;
        }
      }
    }, {
      key: "resetRotation",
      value: function resetRotation() {
        var visualElement = this.options.visualElement;
        if (!visualElement) return;
        // If there's no detected rotation values, we can early return without a forced render.
        var hasRotate = false;
        /**
         * An unrolled check for rotation values. Most elements don't have any rotation and
         * skipping the nested loop and new object creation is 50% faster.
         */
        var latestValues = visualElement.latestValues;
        if (latestValues.rotate || latestValues.rotateX || latestValues.rotateY || latestValues.rotateZ) {
          hasRotate = true;
        }
        // If there's no rotation values, we don't need to do any more.
        if (!hasRotate) return;
        var resetValues = {};
        // Check the rotate value of all axes and reset to 0
        for (var i = 0; i < transformAxes.length; i++) {
          var key = "rotate" + transformAxes[i];
          // Record the rotation and then temporarily set it to 0
          if (latestValues[key]) {
            resetValues[key] = latestValues[key];
            visualElement.setStaticValue(key, 0);
          }
        }
        // Force a render of this element to apply the transform with all rotations
        // set to 0.
        visualElement === null || visualElement === void 0 ? void 0 : visualElement.render();
        // Put back all the values we reset
        for (var _key2 in resetValues) {
          visualElement.setStaticValue(_key2, resetValues[_key2]);
        }
        // Schedule a render for the next frame. This ensures we won't visually
        // see the element with the reset rotate value applied.
        visualElement.scheduleRender();
      }
    }, {
      key: "getProjectionStyles",
      value: function getProjectionStyles() {
        var styleProp = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
        var _a, _b, _c;
        // TODO: Return lifecycle-persistent object
        var styles = {};
        if (!this.instance || this.isSVG) return styles;
        if (!this.isVisible) {
          return {
            visibility: "hidden"
          };
        } else {
          styles.visibility = "";
        }
        var transformTemplate = (_a = this.options.visualElement) === null || _a === void 0 ? void 0 : _a.getProps().transformTemplate;
        if (this.needsReset) {
          this.needsReset = false;
          styles.opacity = "";
          styles.pointerEvents = resolveMotionValue(styleProp.pointerEvents) || "";
          styles.transform = transformTemplate ? transformTemplate(this.latestValues, "") : "none";
          return styles;
        }
        var lead = this.getLead();
        if (!this.projectionDelta || !this.layout || !lead.target) {
          var emptyStyles = {};
          if (this.options.layoutId) {
            emptyStyles.opacity = this.latestValues.opacity !== undefined ? this.latestValues.opacity : 1;
            emptyStyles.pointerEvents = resolveMotionValue(styleProp.pointerEvents) || "";
          }
          if (this.hasProjected && !hasTransform(this.latestValues)) {
            emptyStyles.transform = transformTemplate ? transformTemplate({}, "") : "none";
            this.hasProjected = false;
          }
          return emptyStyles;
        }
        var valuesToRender = lead.animationValues || lead.latestValues;
        this.applyTransformsToTarget();
        styles.transform = buildProjectionTransform(this.projectionDeltaWithTransform, this.treeScale, valuesToRender);
        if (transformTemplate) {
          styles.transform = transformTemplate(valuesToRender, styles.transform);
        }
        var _this$projectionDelta = this.projectionDelta,
          x = _this$projectionDelta.x,
          y = _this$projectionDelta.y;
        styles.transformOrigin = "".concat(x.origin * 100, "% ").concat(y.origin * 100, "% 0");
        if (lead.animationValues) {
          /**
           * If the lead component is animating, assign this either the entering/leaving
           * opacity
           */
          styles.opacity = lead === this ? (_c = (_b = valuesToRender.opacity) !== null && _b !== void 0 ? _b : this.latestValues.opacity) !== null && _c !== void 0 ? _c : 1 : this.preserveOpacity ? this.latestValues.opacity : valuesToRender.opacityExit;
        } else {
          /**
           * Or we're not animating at all, set the lead component to its layout
           * opacity and other components to hidden.
           */
          styles.opacity = lead === this ? valuesToRender.opacity !== undefined ? valuesToRender.opacity : "" : valuesToRender.opacityExit !== undefined ? valuesToRender.opacityExit : 0;
        }
        /**
         * Apply scale correction
         */
        for (var key in scaleCorrectors) {
          if (valuesToRender[key] === undefined) continue;
          var _scaleCorrectors$key = scaleCorrectors[key],
            correct = _scaleCorrectors$key.correct,
            applyTo = _scaleCorrectors$key.applyTo;
          var corrected = correct(valuesToRender[key], lead);
          if (applyTo) {
            var num = applyTo.length;
            for (var i = 0; i < num; i++) {
              styles[applyTo[i]] = corrected;
            }
          } else {
            styles[key] = corrected;
          }
        }
        /**
         * Disable pointer events on follow components. This is to ensure
         * that if a follow component covers a lead component it doesn't block
         * pointer events on the lead.
         */
        if (this.options.layoutId) {
          styles.pointerEvents = lead === this ? resolveMotionValue(styleProp.pointerEvents) || "" : "none";
        }
        return styles;
      }
    }, {
      key: "clearSnapshot",
      value: function clearSnapshot() {
        this.resumeFrom = this.snapshot = undefined;
      }
      // Only run on root
    }, {
      key: "resetTree",
      value: function resetTree() {
        this.root.nodes.forEach(function (node) {
          var _a;
          return (_a = node.currentAnimation) === null || _a === void 0 ? void 0 : _a.stop();
        });
        this.root.nodes.forEach(clearMeasurements);
        this.root.sharedNodes.clear();
      }
    }]);
    return ProjectionNode;
  }();
}
function updateLayout(node) {
  node.updateLayout();
}
function notifyLayoutUpdate(node) {
  var _a, _b, _c;
  var snapshot = ((_a = node.resumeFrom) === null || _a === void 0 ? void 0 : _a.snapshot) || node.snapshot;
  if (node.isLead() && node.layout && snapshot && node.hasListeners("didUpdate")) {
    var _node$layout = node.layout,
      layout = _node$layout.layoutBox,
      measuredLayout = _node$layout.measuredBox;
    var animationType = node.options.animationType;
    var isShared = snapshot.source !== node.layout.source;
    // TODO Maybe we want to also resize the layout snapshot so we don't trigger
    // animations for instance if layout="size" and an element has only changed position
    if (animationType === "size") {
      eachAxis(function (axis) {
        var axisSnapshot = isShared ? snapshot.measuredBox[axis] : snapshot.layoutBox[axis];
        var length = calcLength(axisSnapshot);
        axisSnapshot.min = layout[axis].min;
        axisSnapshot.max = axisSnapshot.min + length;
      });
    } else if (shouldAnimatePositionOnly(animationType, snapshot.layoutBox, layout)) {
      eachAxis(function (axis) {
        var axisSnapshot = isShared ? snapshot.measuredBox[axis] : snapshot.layoutBox[axis];
        var length = calcLength(layout[axis]);
        axisSnapshot.max = axisSnapshot.min + length;
      });
    }
    var layoutDelta = createDelta();
    calcBoxDelta(layoutDelta, layout, snapshot.layoutBox);
    var visualDelta = createDelta();
    if (isShared) {
      calcBoxDelta(visualDelta, node.applyTransform(measuredLayout, true), snapshot.measuredBox);
    } else {
      calcBoxDelta(visualDelta, layout, snapshot.layoutBox);
    }
    var hasLayoutChanged = !isDeltaZero(layoutDelta);
    var hasRelativeTargetChanged = false;
    if (!node.resumeFrom) {
      var relativeParent = node.getClosestProjectingParent();
      /**
       * If the relativeParent is itself resuming from a different element then
       * the relative snapshot is not relavent
       */
      if (relativeParent && !relativeParent.resumeFrom) {
        var parentSnapshot = relativeParent.snapshot,
          parentLayout = relativeParent.layout;
        if (parentSnapshot && parentLayout) {
          var relativeSnapshot = createBox();
          calcRelativePosition(relativeSnapshot, snapshot.layoutBox, parentSnapshot.layoutBox);
          var relativeLayout = createBox();
          calcRelativePosition(relativeLayout, layout, parentLayout.layoutBox);
          if (!boxEquals(relativeSnapshot, relativeLayout)) {
            hasRelativeTargetChanged = true;
          }
        }
      }
    }
    node.notifyListeners("didUpdate", {
      layout: layout,
      snapshot: snapshot,
      delta: visualDelta,
      layoutDelta: layoutDelta,
      hasLayoutChanged: hasLayoutChanged,
      hasRelativeTargetChanged: hasRelativeTargetChanged
    });
  } else if (node.isLead()) {
    (_c = (_b = node.options).onExitComplete) === null || _c === void 0 ? void 0 : _c.call(_b);
  }
  /**
   * Clearing transition
   * TODO: Investigate why this transition is being passed in as {type: false } from Framer
   * and why we need it at all
   */
  node.options.transition = undefined;
}
function propagateDirtyNodes(node) {
  /**
   * Propagate isProjectionDirty. Nodes are ordered by depth, so if the parent here
   * is dirty we can simply pass this forward.
   */
  node.isProjectionDirty || (node.isProjectionDirty = Boolean(node.parent && node.parent.isProjectionDirty));
  /**
   * Propagate isTransformDirty.
   */
  node.isTransformDirty || (node.isTransformDirty = Boolean(node.parent && node.parent.isTransformDirty));
}
function clearSnapshot(node) {
  node.clearSnapshot();
}
function clearMeasurements(node) {
  node.clearMeasurements();
}
function resetTransformStyle(node) {
  var visualElement = node.options.visualElement;
  if (visualElement === null || visualElement === void 0 ? void 0 : visualElement.getProps().onBeforeLayoutMeasure) {
    visualElement.notify("BeforeLayoutMeasure");
  }
  node.resetTransform();
}
function finishAnimation(node) {
  node.finishAnimation();
  node.targetDelta = node.relativeTarget = node.target = undefined;
}
function resolveTargetDelta(node) {
  node.resolveTargetDelta();
}
function calcProjection(node) {
  node.calcProjection();
}
function resetRotation(node) {
  node.resetRotation();
}
function removeLeadSnapshots(stack) {
  stack.removeLeadSnapshot();
}
function mixAxisDelta(output, delta, p) {
  output.translate = mix(delta.translate, 0, p);
  output.scale = mix(delta.scale, 1, p);
  output.origin = delta.origin;
  output.originPoint = delta.originPoint;
}
function mixAxis(output, from, to, p) {
  output.min = mix(from.min, to.min, p);
  output.max = mix(from.max, to.max, p);
}
function mixBox(output, from, to, p) {
  mixAxis(output.x, from.x, to.x, p);
  mixAxis(output.y, from.y, to.y, p);
}
function hasOpacityCrossfade(node) {
  return node.animationValues && node.animationValues.opacityExit !== undefined;
}
var defaultLayoutTransition = {
  duration: 0.45,
  ease: [0.4, 0, 0.1, 1]
};
function mountNodeEarly(node, elementId) {
  /**
   * Rather than searching the DOM from document we can search the
   * path for the deepest mounted ancestor and search from there
   */
  var searchNode = node.root;
  for (var i = node.path.length - 1; i >= 0; i--) {
    if (Boolean(node.path[i].instance)) {
      searchNode = node.path[i];
      break;
    }
  }
  var searchElement = searchNode && searchNode !== node.root ? searchNode.instance : document;
  var element = searchElement.querySelector("[data-projection-id=\"".concat(elementId, "\"]"));
  if (element) node.mount(element, true);
}
function roundAxis(axis) {
  axis.min = Math.round(axis.min);
  axis.max = Math.round(axis.max);
}
function roundBox(box) {
  roundAxis(box.x);
  roundAxis(box.y);
}
function shouldAnimatePositionOnly(animationType, snapshot, layout) {
  return animationType === "position" || animationType === "preserve-aspect" && !isNear(aspectRatio(snapshot), aspectRatio(layout), 0.2);
}
export { createProjectionNode, mixAxis, mixAxisDelta, mixBox, propagateDirtyNodes };